@if (user) {
@if (presentation === 'floating' && displayMode === 'floating') {
<button
  type="button"
  class="bw-ai-assistant__launcher"
  [class.is-open]="isOpen"
  aria-label="Open BizzWeaver AI"
  [attr.aria-expanded]="isOpen"
  (click)="toggle()"
>
  <span>AI</span>
  @if (isStreaming) {
    <i aria-hidden="true"></i>
  }
</button>
}

@if (isOpen || presentation === 'page') {
  <aside class="bw-ai-assistant" [class.bw-ai-assistant--page]="presentation === 'page'" role="dialog" aria-modal="false" aria-label="BizzWeaver AI assistant">
    <header class="bw-ai-assistant__header">
      <div>
        <p>BizzWeaver AI</p>
      </div>
      <div class="bw-ai-assistant__header-actions">
        <button type="button" class="bw-ai-assistant__icon-button" title="New conversation" aria-label="New conversation" [disabled]="isStreaming || isConversationLoading" (click)="newConversation()">+</button>
        <button type="button" class="bw-ai-assistant__icon-button" title="Conversation history" aria-label="Conversation history" [class.is-active]="showHistory" (click)="showHistory = !showHistory">&#9776;</button>
        @if (presentation === 'floating') {
          <button type="button" class="bw-ai-assistant__icon-button" title="Assistant display and apply settings" aria-label="Assistant display and apply settings" [class.is-active]="showPreferences" (click)="showPreferences = !showPreferences">&#9881;</button>
          <button type="button" class="bw-ai-assistant__icon-button" title="Close BizzWeaver AI" aria-label="Close BizzWeaver AI" (click)="toggle()">&times;</button>
        }
      </div>
    </header>

    <div class="bw-ai-assistant__content">
    @if (showPreferences) {
      <section class="bw-ai-assistant__preferences" aria-label="Assistant controls">
        <strong>Assistant controls</strong>
        <label>Display
          <select [ngModel]="displayMode" (ngModelChange)="setDisplayMode($event)">
            <option value="floating">Floating panel</option>
            <option value="side">Right side panel</option>
            <option value="bottom">Bottom panel</option>
          </select>
        </label>
        <label>Apply mode
          <select [ngModel]="applyMode" (ngModelChange)="setApplyMode($event)">
            <option value="review">Review every change</option>
            <option value="auto">Auto-apply safe draft changes</option>
          </select>
        </label>
        <small>Auto mode never publishes, restores, or deletes. Those actions always require review.</small>
        <div>
          <button type="button" (click)="openFullChat()">Open full chat</button>
          <button type="button" (click)="showPreferences = false">Close</button>
        </div>
      </section>
    }

    @if (errorMessage) {
      <div class="bw-ai-assistant__notice bw-ai-assistant__notice--error" role="alert">{{ errorMessage }}</div>
    }
    @if (statusMessage) {
      <div class="bw-ai-assistant__notice" aria-live="polite">{{ statusMessage }}</div>
    }

    <div class="bw-ai-assistant__metering">
      @if (metering?.ownerBypass) {
        <div class="bw-ai-assistant__owner-metering">
          <strong>Owner test access</strong>
          <span>Usage is measured; plan, prepaid, and rate limits are bypassed.</span>
        </div>
      } @else {
        <div>
          <strong>{{ formatTokens(metering?.remainingTokens ?? 0) }}</strong>
          <span>included tokens remaining</span>
        </div>
        <div>
          <strong>{{ formatTokens(metering?.prepaidTokens ?? 0) }}</strong>
          <span>prepaid tokens</span>
        </div>
      }
      <button type="button" (click)="openUsagePage()">Usage &amp; credits</button>
    </div>

    @if (showCredits) {
      <section class="bw-ai-assistant__credits" aria-label="BizzWeaver AI usage and prepaid credits">
        <div class="bw-ai-assistant__credits-summary">
          <span>This month</span>
          <strong>{{ formatTokens(metering?.usedTokens ?? 0) }} tokens</strong>
          @if (metering?.ownerBypass) {
            <small>Owner test access is active. Provider usage is measured but no plan or prepaid balance is consumed.</small>
          } @else {
            <small>{{ formatTokens(metering?.remainingTokens ?? 0) }} included tokens and {{ formatTokens(metering?.prepaidTokens ?? 0) }} prepaid tokens remain.</small>
          }
          <small>OpenAI provider estimate: {{ formatUsdFromNano(metering?.estimatedCostNanoUsd ?? 0) }}.</small>
        </div>
        <p>Prepaid tokens are charged before use through Paddle. They are added only after Paddle confirms the completed payment; no end-of-month overage invoice is created.</p>
        @if (creditPacks.length === 0) {
          <p>No prepaid AI token packs are available yet. The BizzWeaver owner must publish a pack before credits can be purchased.</p>
        } @else {
          <div class="bw-ai-assistant__credit-packs">
            @for (pack of creditPacks; track pack.publicId) {
              <article class="bw-ai-assistant__credit-pack">
                <div>
                  <strong>{{ pack.label }}</strong>
                  <span>{{ formatTokens(pack.tokenAmount) }} tokens</span>
                  @if (pack.description) {
                    <small>{{ pack.description }}</small>
                  }
                </div>
                <button type="button" class="bw-ai-assistant__button" [disabled]="isCreditCheckoutBusy" (click)="buyCreditPack(pack)">
                  {{ isCreditCheckoutBusy ? 'Opening checkout...' : ('Buy ' + formatCreditPackPrice(pack)) }}
                </button>
              </article>
            }
          </div>
        }
        <div class="bw-ai-assistant__credits-actions">
          <button type="button" class="bw-ai-assistant__button bw-ai-assistant__button--quiet" (click)="showCredits = false">Close</button>
        </div>
      </section>
    }

    <div class="bw-ai-assistant__workspace">
      @if (showHistory) {
        <section class="bw-ai-assistant__history" aria-label="Conversation history">
          <div class="bw-ai-assistant__history-title">
            <span>Conversations</span>
            <button type="button" (click)="newConversation()" [disabled]="isStreaming || isConversationLoading">New</button>
          </div>
          @if (conversations.length === 0) {
            <p class="bw-ai-assistant__empty">Your saved conversations will appear here.</p>
          } @else {
            <div class="bw-ai-assistant__conversation-list">
              @for (conversation of conversations; track trackConversation($index, conversation)) {
                <article class="bw-ai-assistant__conversation" [class.is-active]="conversation.publicId === activeConversationId">
                  @if (renameConversationId === conversation.publicId) {
                    <input [(ngModel)]="renameDraft" aria-label="Conversation title" (keydown.enter)="saveRename()" />
                    <div class="bw-ai-assistant__conversation-actions">
                      <button type="button" (click)="saveRename()">Save</button>
                      <button type="button" (click)="cancelRename()">Cancel</button>
                    </div>
                  } @else {
                    <button type="button" class="bw-ai-assistant__conversation-select" (click)="selectConversation(conversation.publicId)">
                      <strong>{{ conversation.title }}</strong>
                      <small>{{ conversation.preview || 'No response yet' }}</small>
                    </button>
                    <div class="bw-ai-assistant__conversation-actions">
                      <button type="button" title="Rename conversation" aria-label="Rename conversation" (click)="beginRename(conversation)">Edit</button>
                      <button type="button" title="Delete conversation" aria-label="Delete conversation" (click)="requestDeleteConversation(conversation)">Delete</button>
                    </div>
                  }
                </article>
              }
            </div>
          }
        </section>
      } @else {
        <section class="bw-ai-assistant__chat" [attr.aria-busy]="isConversationLoading">
          @if (isConversationLoading) {
            <div class="bw-ai-assistant__conversation-loading" role="status">Loading conversation...</div>
          } @else {
          <div #messageList class="bw-ai-assistant__messages" aria-live="polite">
            @if (messages.length === 0) {
              <div class="bw-ai-assistant__welcome">
                <strong>Build with a review step.</strong>
                <p>Ask for a website, page, component, design review, version, or deployment. Every workspace change is shown for your approval before it is applied.</p>
              </div>
            }
            @for (message of messages; track trackMessage($index, message)) {
              <article class="bw-ai-assistant__message" [class.bw-ai-assistant__message--user]="message.role === 'user'">
                <span class="bw-ai-assistant__message-role">
                  {{ message.role === 'user' ? 'You' : 'BizzWeaver AI' }}
                  <small>{{ formatTimestamp(message.createdAt) }}</small>
                </span>
                @if (message.content) {
                  <div class="bw-ai-assistant__message-content" [innerHTML]="renderMarkdown(message.content)"></div>
                } @else if (isStreaming && message.role === 'assistant') {
                  <div class="bw-ai-assistant__typing"><i></i><i></i><i></i></div>
                }
                @if (message.images.length > 0) {
                  <div class="bw-ai-assistant__message-images">
                    @for (image of message.images; track image.publicId) {
                      <figure [class.is-loading]="image.previewState !== 'ready'" [class.is-failed]="image.previewState === 'failed'">
                        @if (image.previewUrl) {
                          <img [src]="image.previewUrl" [alt]="image.fileName" (load)="markMessageImagePreviewLoaded(image)" (error)="markMessageImagePreviewFailed(image)" />
                        }
                        @if (image.previewState !== 'ready') {
                          <div class="bw-ai-assistant__image-placeholder" [attr.role]="image.previewState === 'failed' ? 'alert' : 'status'">
                            {{ image.previewState === 'failed' ? 'Image unavailable' : 'Loading image...' }}
                          </div>
                        }
                      </figure>
                    }
                  </div>
                }
                @if (message.toolActivities?.length) {
                  <div class="bw-ai-assistant__tool-activity" aria-label="Workspace activity">
                    @for (activity of message.toolActivities; track activity.id) {
                      <span [class.is-running]="activity.phase === 'running'" [class.is-failed]="activity.phase === 'failed'">
                        {{ activity.phase === 'complete' ? 'Done' : activity.phase === 'failed' ? 'Could not complete' : 'Working' }}: {{ activity.label }}
                      </span>
                    }
                  </div>
                }
                @if (pendingActions(message).length > 1) {
                  <button type="button" class="bw-ai-assistant__button bw-ai-assistant__button--apply-all" (click)="requestApplyAll(message)">Review and apply all {{ pendingActions(message).length }} changes</button>
                }
                @for (action of message.actions; track action.id) {
                  <section class="bw-ai-assistant__action-card">
                    <span>{{ isAutomaticAction(action) ? 'Auto apply' : 'Review required' }}</span>
                    <strong>{{ action.title }}</strong>
                    <p>{{ action.summary }}</p>
                    <button
                      type="button"
                      class="bw-ai-assistant__button"
                      [disabled]="appliedActions.has(action.id) || applyingActions.has(action.id)"
                      (click)="requestApplyAction(action)"
                    >{{ actionButtonLabel(action) }}</button>
                  </section>
                }
                @if (message.usage) {
                  <small class="bw-ai-assistant__usage">{{ formatTokens(message.usage.totalTokens) }} tokens · {{ usageSummary(message.usage) }}</small>
                }
              </article>
            }
          </div>
          }

          <div class="bw-ai-assistant__composer">
            @if (pendingImages.length > 0) {
              <div class="bw-ai-assistant__pending-images">
                @for (image of pendingImages; track image.id) {
                  <figure [class.is-loading]="image.state !== 'ready' || !image.previewLoaded" [class.is-failed]="image.state === 'failed'">
                    @if (image.state === 'ready' && image.image; as readyImage) {
                      <img [src]="readyImage.dataUrl" [alt]="readyImage.fileName" (load)="markPendingImagePreviewLoaded(image.id)" />
                    }
                    @if (image.state !== 'ready' || !image.previewLoaded) {
                      <div class="bw-ai-assistant__image-placeholder" [attr.role]="image.state === 'failed' ? 'alert' : 'status'">
                        {{ image.state === 'failed' ? 'Could not optimize' : 'Optimizing image...' }}
                      </div>
                    }
                    <button type="button" aria-label="Remove image" (click)="removePendingImage(image.id)">&times;</button>
                  </figure>
                }
              </div>
            }
            <textarea
              [(ngModel)]="draft"
              rows="3"
              maxlength="12000"
              placeholder="Describe what you want to create or improve..."
              aria-label="Message BizzWeaver AI"
              [disabled]="isStreaming || isConversationLoading"
              (keydown)="handleInputKeydown($event)"
              (paste)="handlePaste($event)"
            ></textarea>
            <input #fileInput type="file" accept="image/*" multiple hidden (change)="chooseImages($event)" />
            <div class="bw-ai-assistant__composer-actions">
              <button type="button" class="bw-ai-assistant__attach" [disabled]="isStreaming || isConversationLoading" (click)="triggerImagePicker()">Attach image</button>
              @if (isStreaming) {
                <button type="button" class="bw-ai-assistant__button bw-ai-assistant__button--danger" (click)="stop()">Stop</button>
              } @else {
                <button type="button" class="bw-ai-assistant__button" [disabled]="isConversationLoading || hasPendingImageProcessing || hasPendingImageFailures || (!draft.trim() && pendingImages.length === 0) || !metering?.canContinue" (click)="send()">Send</button>
              }
            </div>
          </div>
        </section>
      }
    </div>
    </div>
  </aside>
}

@if (confirmation; as pending) {
  <div class="bw-ai-assistant__confirmation-backdrop" (click)="dismissConfirmation()">
    <section class="bw-ai-assistant__confirmation" role="alertdialog" aria-modal="true" aria-labelledby="bw-ai-confirmation-title" (click)="$event.stopPropagation()">
      <h2 id="bw-ai-confirmation-title">{{ pending.kind === 'delete-conversation' ? 'Delete conversation?' : pending.kind === 'apply-actions' ? ('Apply ' + pending.actions.length + ' proposed changes?') : 'Apply this proposed change?' }}</h2>
      <p>{{ pending.kind === 'delete-conversation' ? 'This permanently removes the saved conversation and its private assistant images.' : pending.kind === 'apply-actions' ? 'Each proposed change will be applied in order. Draft changes still require a separate publish action before visitors see them.' : pending.action.summary }}</p>
      @if (pending.kind === 'apply-action' || pending.kind === 'apply-actions') {
        <small>The assistant cannot apply this by itself. This confirmation uses the same authenticated BizzWeaver API as the product.</small>
      }
      <div class="bw-ai-assistant__confirmation-actions">
        <button type="button" class="bw-ai-assistant__button bw-ai-assistant__button--quiet" (click)="dismissConfirmation()">Cancel</button>
        <button type="button" class="bw-ai-assistant__button" [class.bw-ai-assistant__button--danger]="pending.kind === 'delete-conversation'" (click)="confirmPendingAction()">
          {{ pending.kind === 'delete-conversation' ? 'Delete permanently' : pending.kind === 'apply-actions' ? ('Apply ' + pending.actions.length + ' changes') : 'Apply change' }}
        </button>
      </div>
    </section>
  </div>
}
}
